import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";

import {
  listarEventos,
  me,
  eliminarSesion,
} from "../api";
import "./CalendarPage.css";

const VIEWS = [
  { key: "dayGridMonth", label: "Mes" },
  { key: "timeGridWeek", label: "Semana" },
  { key: "timeGridDay", label: "Día" },
];

const FESTIVOS_JEREZ = [
  { title: "Feria del Caballo", date: "2025-05-11", className: "local-jerez" },
  { title: "Corpus Christi", date: "2025-06-19", className: "local-jerez" },
];

/* ✅ Renderizado adaptativo */
function renderEventContent(eventInfo) {
  const { event, handleDeleteEvent, isAdmin } = eventInfo;

  if (event.extendedProps?.holiday) {
    return (
      <div className="fc-event-holiday">
        <strong>{event.title}</strong>
      </div>
    );
  }

  const start = event.start
    ? event.start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";
  const end = event.end
    ? event.end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";

  const paciente = event.extendedProps?.paciente || event.title;
  const servicio = event.extendedProps?.service;
  const profesional = event.extendedProps?.professionalName;

  // ahora la "specialty" real es code (ej: psicologia-adultos)
  const specialty = String(event.extendedProps?.specialty || "").toLowerCase();

  const getIcon = () => {
    if (specialty.includes("psico")) return "🧠";
    if (specialty.includes("logo")) return "💬";
    if (specialty.includes("administracion")) return "📄";
    if (specialty.includes("nutri")) return "🥗";
    return "📅";
  };

  const eventId = event.id;

  const handleDelete = (e) => {
    e.stopPropagation();
    if (typeof handleDeleteEvent === "function") handleDeleteEvent(eventId);
  };

  return (
    <div className="fc-event-custom" data-specialty={specialty}>
      <div className="event-header">
        <strong>
          {start} - {end}
        </strong>

        {/* 👇 Solo ADMIN ve la ❌ */}
        {isAdmin && (
          <button
            className="delete-btn"
            title="Eliminar cita"
            onClick={handleDelete}
          >
            ✕
          </button>
        )}
      </div>

      <div className="event-desktop">
        <div className="patient-name">{paciente}</div>
        {servicio && <div className="service-label">{servicio}</div>}
        {profesional && (
          <div className="professional-label">{profesional}</div>
        )}
      </div>

      <div className="event-mobile">
        <span className="event-icon">{getIcon()}</span> {start}
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const [user, setUser] = useState(null);

  const [view, setView] = useState("dayGridMonth");

const PROFESSIONAL_CALENDARS = [
  {
    professionalId: 2,
    specialty: "psicologia",
    label: "Psicología · Teresa",
  },
  {
    professionalId: 17,
    specialty: "psicologia",
    label: "Psicología · Marina",
  },
  {
    professionalId: 18,
    specialty: "psicologia",
    label: "Psicología · Ana",
  },
  {
    professionalId: 3,
    specialty: "logopeda",
    label: "Logopedia · Mercedes",
  },
  {
    professionalId: 19,
    specialty: "logopeda",
    label: "Logopedia · Beatriz",
  },
  {
    professionalId: 7,
    specialty: "nutricion",
    label: "Nutrición · María",
  },
  {
    professionalId: 1,
    specialty: "administracion",
    label: "Administración · Américo",
  },
];

  const [selectedProfessionalId, setSelectedProfessionalId] = useState("");

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const calRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const r = await me();
      const u = r?.user || null;
      setUser(u);

      if (!u) return;

      if (u.role === "admin") {
        setSelectedProfessionalId("2");
        return;
      }

      setSelectedProfessionalId(String(u.id));
    })();
  }, []);

  const isAdmin = user?.role === "admin";

  const eventSource = useMemo(() => {
    return async (info, success, failure) => {
      try {
        if (!selectedProfessionalId) {
          success([]);
          setEvents([]);
          return;
        }

        setLoading(true);

        const selectedCalendar = PROFESSIONAL_CALENDARS.find(
          (item) =>
            Number(item.professionalId) === Number(selectedProfessionalId)
        );

        const items = await listarEventos({
          professional_id: selectedProfessionalId,
          specialty: selectedCalendar?.specialty || "",
          calendar: selectedCalendar?.specialty || "",
          start: info.startStr.slice(0, 10),
          end: info.endStr.slice(0, 10),
        });

        success(items);
        setEvents(items);
      } catch (e) {
        console.error(e);
        failure(e);
      } finally {
        setLoading(false);
      }
    };
  }, [selectedProfessionalId]);

  // Festivos
  const festivosSource = useMemo(() => {
    return async (info, success, failure) => {
      try {
        const year = new Date(info.startStr).getFullYear();
        const res = await fetch(
          `https://date.nager.at/api/v3/PublicHolidays/${year}/ES`,
        );
        if (!res.ok) throw new Error("Error al cargar festivos");
        const data = await res.json();

        const festivos = data
          .filter((f) => {
            if (!f.counties) return true;
            if (f.counties.some((c) => c === "AN")) return true;
            if (f.counties.some((c) => c === "AN-CA")) return true;
            return false;
          })
          .map((f) => {
            let className = "festivo-nacional";
            if (f.counties?.some((c) => c === "AN-CA"))
              className = "festivo-cadiz";
            else if (f.counties?.some((c) => c === "AN"))
              className = "festivo-andalucia";
            return {
              title: f.localName,
              date: f.date,
              className,
              holiday: true,
            };
          });

        const locales = FESTIVOS_JEREZ.map((f) => ({ ...f, holiday: true }));
        success([...festivos, ...locales]);
      } catch (e) {
        console.error(e);
        failure(e);
      }
    };
  }, []);

  // ✅ Delete usando tu API (HTTPS + DELETE + cookie)
  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("¿Seguro que quieres eliminar esta cita?")) return;
    try {
      const data = await eliminarSesion(eventId);
      if (!data?.ok)
        throw new Error(data?.error || "Error al eliminar la cita");
      calRef.current?.getApi()?.refetchEvents();
    } catch (err) {
      console.error(err);
      alert(err.message || "No se pudo eliminar la cita ❌");
    }
  };

  const handleEventClick = (clickInfo) => {
    const pacienteId = clickInfo.event.extendedProps?.pacienteId;
    if (pacienteId) navigate(`/paciente/${pacienteId}`);
  };

  const handleEventMount = (info) => {
    const props = info.event.extendedProps;
    const startDate = new Date(info.event.start);
    const endDate = new Date(info.event.end);

    const start = startDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    const end = endDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    const duracionMin = Math.round((endDate - startDate) / (1000 * 60));
    const duracionTexto =
      duracionMin >= 60
        ? `${Math.floor(duracionMin / 60)}h ${duracionMin % 60}min`
        : `${duracionMin} min`;

    const content = `
      <strong>${props.paciente || info.event.title}</strong><br>
      <b>Servicio:</b> ${props.service || "—"}<br>
      <b>Profesional:</b> ${props.professionalName || "—"}<br>
      <b>Hora:</b> ${start} - ${end}<br>
      <b>Duración:</b> ${duracionTexto}<br>
      <b>Especialidad:</b> ${props.specialty || "—"}
    `;

    tippy(info.el, {
      content,
      allowHTML: true,
      theme: "light-border",
      placement: "top",
    });
  };

  const handleChangeView = (val) => {
    setView(val);
    calRef.current?.getApi()?.changeView(val);
  };

  useEffect(() => {
    calRef.current?.getApi()?.refetchEvents();
  }, [selectedProfessionalId]);

  if (!user) return <div style={{ padding: "1rem" }}>Cargando…</div>;

  const getCalendarLabel = () => {
    const selected = PROFESSIONAL_CALENDARS.find(
      (item) =>
        Number(item.professionalId) === Number(selectedProfessionalId)
    );

    return selected?.label || user?.nombre || "Calendario";
  };

  return (
    <div className="cal-wrap">
      <div className="cal-toolbar">
        <div className="left">
          <select
            value={view}
            onChange={(e) => handleChangeView(e.target.value)}
            aria-label="Seleccionar vista"
          >
            {[
              { key: "dayGridMonth", label: "Mes" },
              { key: "timeGridWeek", label: "Semana" },
              { key: "timeGridDay", label: "Día" },
            ].map((v) => (
              <option key={v.key} value={v.key}>
                {v.label}
              </option>
            ))}
          </select>

          {isAdmin ? (
            <select
              value={selectedProfessionalId}
              onChange={(e) => setSelectedProfessionalId(e.target.value)}
              aria-label="Seleccionar profesional"
            >
              {PROFESSIONAL_CALENDARS.map((item) => (
                <option
                  key={item.professionalId}
                  value={item.professionalId}
                >
                  {item.label}
                </option>
              ))}
            </select>
          ) : (
            <span className="badge">{getCalendarLabel()}</span>
          )}
        </div>

        <div className="right">
          {loading ? (
            <span className="muted">Cargando eventos…</span>
          ) : (
            <span className="muted">{events.length} evento(s)</span>
          )}
        </div>
      </div>

      <div className="cal-card">
        <FullCalendar
          ref={calRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          locale={esLocale}
          initialView={view}
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "",
          }}
          datesSet={(arg) => {
            if (arg.view?.type && arg.view.type !== view)
              setView(arg.view.type);
          }}
          eventSources={[eventSource, festivosSource]}
          eventContent={(info) =>
            renderEventContent({ ...info, handleDeleteEvent, isAdmin })
          }
          eventClick={handleEventClick}
          eventDidMount={handleEventMount}
          navLinks={true}
          nowIndicator={true}
          height="auto"
          dayMaxEventRows={3}
          editable={false}
          selectable={false}
          slotMinTime="09:00:00"
          slotMaxTime="21:00:00"
        />
      </div>
    </div>
  );
}
